import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "veloura_cart_v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const removeItem = useCallback((product_id) => {
        setItems((prev) => prev.filter((i) => i.product_id !== product_id));
    }, []);

    const addItem = useCallback((product, quantity = 1) => {
        setItems((prev) => {
            const found = prev.find((i) => i.product_id === product.id);
            if (found) {
                return prev.map((i) =>
                    i.product_id === product.id
                        ? { ...i, quantity: i.quantity + quantity }
                        : i,
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    image: product.image,
                    quantity,
                },
            ];
        });
        setOpen(true);
    }, []);

    const updateQty = useCallback(
        (product_id, quantity) => {
            if (quantity <= 0) {
                removeItem(product_id);
                return;
            }
            setItems((prev) =>
                prev.map((i) =>
                    i.product_id === product_id ? { ...i, quantity } : i,
                ),
            );
        },
        [removeItem],
    );

    const clearCart = useCallback(() => setItems([]), []);

    const subtotal = useMemo(
        () => items.reduce((s, i) => s + i.price * i.quantity, 0),
        [items],
    );
    const count = useMemo(
        () => items.reduce((s, i) => s + i.quantity, 0),
        [items],
    );
    const shipping = subtotal > 0 && subtotal < 2500 ? 99 : 0;
    const total = subtotal + shipping;

    const value = useMemo(
        () => ({
            items,
            addItem,
            updateQty,
            removeItem,
            clearCart,
            subtotal,
            shipping,
            total,
            count,
            open,
            setOpen,
        }),
        [
            items,
            addItem,
            updateQty,
            removeItem,
            clearCart,
            subtotal,
            shipping,
            total,
            count,
            open,
        ],
    );

    return (
        <CartContext.Provider value={value}>{children}</CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
